import { useEffect, useState, useRef, useCallback } from "react";
import { AlertTriangle, ExternalLink, Lock } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { DualPayLinks, railUrls } from "./payments/DualPayLinks";

const POLL_MS = 30_000;       // sync with server every 30s
const TICK_MS = 1_000;        // local countdown every second

const fmt = (secs) => {
  const s = Math.max(0, Math.floor(secs));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, "0")}`;
};

/**
 * Glowing red 15-minute countdown that mounts globally.  When the local
 * timer hits zero, we do a hard boot: clear cookies, discard the session
 * in-memory and mount the full-screen "Access Revoked" overlay in place
 * of the banner.
 *
 * The banner is invisible for admins and for users without an active
 * `registration_deadline_at` — no cost to non-affected sessions.
 */
export function RegistrationDeadlineBanner() {
  const { user, api, logout } = useAuth();
  const [state, setState] = useState(null);         // {active, deadline_at, remaining_seconds, ...}
  const [tick, setTick] = useState(0);              // local counter to force re-render
  const [revoked, setRevoked] = useState(false);
  const bootedRef = useRef(false);

  const poll = useCallback(async () => {
    try {
      const { data } = await api.get("/registration-deadline/me");
      setState(data);
      if (!data?.active) setRevoked(false);
    } catch (e) {
      // 401 with detail.code='registration_cancelled' → hard boot immediately.
      if (e?.response?.status === 401 &&
          e?.response?.data?.detail?.code === "registration_cancelled") {
        setRevoked(true);
      }
    }
  }, [api]);

  useEffect(() => {
    if (!user || user.role === "admin") {
      setState(null); setRevoked(false);
      return;
    }
    poll();
    const id = setInterval(poll, POLL_MS);
    return () => clearInterval(id);
  }, [user, poll]);

  useEffect(() => {
    if (!state?.active) return;
    const id = setInterval(() => setTick((t) => t + 1), TICK_MS);
    return () => clearInterval(id);
  }, [state?.active]);

  const remaining = (() => {
    if (!state?.active || !state.deadline_at) return null;
    const deadline = new Date(state.deadline_at).getTime();
    return Math.floor((deadline - Date.now()) / 1000);
  })();

  // Hard-boot the browser session when the local timer hits zero.
  useEffect(() => {
    if (remaining === null || bootedRef.current) return;
    if (remaining <= 0 && state?.active) {
      bootedRef.current = true;
      setRevoked(true);
      // httpOnly cookies can't be cleared from JS, so we ask the backend
      // to drop them server-side.  Fire-and-forget — the overlay stays
      // regardless of the network outcome.
      (async () => {
        try { await api.post("/auth/logout"); } catch {}
      })();
    }
  }, [remaining, state?.active, api]);

  if (revoked) return <AccessRevokedOverlay email={user?.email} logout={logout} />;
  if (!user || user.role === "admin" || !state?.active) return null;
  const secs = remaining ?? state.remaining_seconds ?? 0;
  const critical = secs <= 120;

  return (
    <div
      data-testid="registration-deadline-banner"
      className="fixed top-0 left-0 right-0 z-[80] border-b border-[#C0392B]/70 bg-gradient-to-r from-[#C0392B]/[0.18] via-[#7a1e15]/[0.22] to-[#C0392B]/[0.18] backdrop-blur-xl"
      style={{
        boxShadow: critical
          ? "0 0 40px rgba(192,57,43,0.85), inset 0 0 30px rgba(192,57,43,0.4)"
          : "0 0 24px rgba(192,57,43,0.55), inset 0 0 20px rgba(192,57,43,0.25)",
        animation: critical ? "vinci-pulse-hot 1.1s ease-in-out infinite" : "vinci-pulse-warn 2.3s ease-in-out infinite",
      }}
    >
      <style>{`
        @keyframes vinci-pulse-hot {
          0%,100% { box-shadow: 0 0 40px rgba(192,57,43,0.85), inset 0 0 30px rgba(192,57,43,0.35); }
          50%     { box-shadow: 0 0 70px rgba(192,57,43,1.0),  inset 0 0 40px rgba(192,57,43,0.55); }
        }
        @keyframes vinci-pulse-warn {
          0%,100% { box-shadow: 0 0 24px rgba(192,57,43,0.55), inset 0 0 20px rgba(192,57,43,0.20); }
          50%     { box-shadow: 0 0 42px rgba(192,57,43,0.85), inset 0 0 28px rgba(192,57,43,0.35); }
        }
      `}</style>
      <div className="max-w-7xl mx-auto px-6 py-3 flex flex-col md:flex-row md:items-center gap-3">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <span className="grid place-items-center w-9 h-9 rounded-full bg-[#C0392B] text-white shrink-0">
            <AlertTriangle size={18} />
          </span>
          <div className="min-w-0">
            <div className="font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#FF9AA0]">
              Ecosystem Registration Deadline
            </div>
            <div className="font-body text-sm text-[#FFEDEB] leading-snug mt-0.5">
              <span className="text-[#FFCFCB]">Complete payment in </span>
              <span
                data-testid="deadline-countdown"
                className="font-serif tracking-tight text-white text-lg md:text-xl"
                style={{ textShadow: "0 0 10px rgba(255,120,110,0.9)" }}
              >
                {fmt(secs)}
              </span>
              <span className="text-[#FFCFCB]"> or your account will be revoked.</span>
            </div>
          </div>
        </div>
        {(state.payment_link_url || state.pay_urls?.phonepe) ? (
          <div className="self-start md:self-center" data-testid="deadline-pay-now">
            <DualPayLinks testId="deadline-pay" size="sm" light urls={railUrls(state)} amountLabel={`₹${(state.fee_inr || 41680).toLocaleString("en-IN")}`} />
          </div>
        ) : (
          <a
            data-testid="deadline-open-vault"
            href="/account/documents"
            className="rounded-full bg-white text-[#7a1e15] hover:bg-[#FFEDEB] px-5 py-2 font-grotesk text-[10px] uppercase tracking-[0.22em] transition-colors self-start md:self-center whitespace-nowrap"
          >
            Open vault to pay
          </a>
        )}
      </div>
    </div>
  );
}

/**
 * Full-screen authoritative revocation overlay.  Renders on top of the
 * entire app.  ALL navigation links are disabled — only the mailto to the
 * Onboarding Desk is clickable.  This is a terminal state; a page reload
 * bounces the user to /login where they're refused with
 * `code=membership_expired`.
 */
function AccessRevokedOverlay({ email }) {
  const stop = (e) => { e.preventDefault(); e.stopPropagation(); };

  useEffect(() => {
    // Kill all pointer events except our escape-hatch mailto.
    const b = document.body;
    const prev = b.style.overflow;
    b.style.overflow = "hidden";
    // Best-effort intercept of every click that isn't inside our overlay.
    const intercept = (e) => {
      if (!e.target.closest?.("[data-testid=access-revoked-overlay]")) {
        e.preventDefault(); e.stopPropagation();
      }
    };
    document.addEventListener("click", intercept, true);
    return () => {
      b.style.overflow = prev;
      document.removeEventListener("click", intercept, true);
    };
  }, []);

  return (
    <div
      data-testid="access-revoked-overlay"
      role="alertdialog"
      aria-labelledby="access-revoked-title"
      onClickCapture={stop}
      className="fixed inset-0 z-[100] bg-[#0a0000] flex items-center justify-center px-6"
      style={{
        background: "radial-gradient(circle at 50% 40%, rgba(192,57,43,0.45), rgba(10,0,0,0.98) 65%)",
      }}
    >
      <div className="max-w-2xl w-full text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#C0392B] text-white mb-6 shadow-[0_0_50px_rgba(192,57,43,0.9)]">
          <Lock size={30} />
        </div>
        <div className="font-grotesk text-[11px] uppercase tracking-[0.5em] text-[#FF9AA0]">
          Vinci Trade Network
        </div>
        <h1
          id="access-revoked-title"
          data-testid="access-revoked-title"
          className="font-serif text-4xl md:text-6xl mt-4 text-white leading-[1.05]"
          style={{ textShadow: "0 0 30px rgba(255,80,68,0.8)" }}
        >
          Access Revoked
        </h1>
        <p className="font-body text-sm md:text-base text-[#FFCFCB] mt-6 leading-relaxed">
          The <b>15-minute deadline</b> for your Certificate of Authorisation has elapsed without payment.
          Under the Vinci Trade Network Onboarding Charter, your ecosystem access is now <b>cancelled</b> and
          your login has been permanently locked.
        </p>
        <p className="font-body text-sm md:text-base text-[#FFCFCB] mt-3 leading-relaxed">
          This is <b>not a suspension</b>. It is a full revocation. You may not re-enter with{" "}
          {email ? <span className="font-mono text-white">{email}</span> : "this email"}.
          A fresh registration with a new email is required — identity verification, agreement
          execution and the ₹41,680 authorisation fee from the beginning.
        </p>
        <div className="mt-10 border border-[#C0392B]/50 rounded-sm p-4 bg-[#3d0e08]/60 max-w-md mx-auto">
          <div className="overline text-[#FF9AA0]">Appeals & Restoration</div>
          <p className="font-body text-sm text-[#FFEDEB] mt-2 leading-relaxed">
            Requests may be made within 30 days to our Onboarding Desk.
          </p>
          <a
            data-testid="access-revoked-contact"
            href="mailto:vinci.connect@vincitradeasia.com?subject=Appeal%20—%20Access%20Revoked"
            className="mt-4 inline-flex items-center gap-2 rounded-full bg-white text-[#7a1e15] hover:bg-[#FFEDEB] px-6 py-2.5 font-grotesk text-[10px] uppercase tracking-[0.2em] transition-colors"
          >
            Contact Onboarding
          </a>
        </div>
        <p className="font-grotesk text-[10px] uppercase tracking-[0.3em] text-[#8A6F6C] mt-10">
          All navigation has been disabled for this session.
        </p>
      </div>
    </div>
  );
}
